import React, { useState } from 'react';
import { QUMRAN_DATA } from './archiveData';

export const QumranInversion: React.FC = () => {
  const [activeNetId, setActiveNetId] = useState<string>('hon');
  const [copiedProclamation, setCopiedProclamation] = useState<boolean>(false);

  const currentNet = QUMRAN_DATA.nets.find(n => n.id === activeNetId) || QUMRAN_DATA.nets[0];

  const handleCopy = async (text: string) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedProclamation(true);
      setTimeout(() => setCopiedProclamation(false), 2000);
    } catch {
      // Fallback
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" id="qumran-inversion-section">
      <div className="border-b border-iron-dark pb-4">
        <div className="flex items-center gap-2 text-xs font-mono text-gold-ancient uppercase tracking-widest">
          <span>LIBER TERTIUS</span>
          <span>•</span>
          <span>QUMRAN INVERSION</span>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-parchment-light uppercase mt-1">
          {QUMRAN_DATA.title}
        </h2>
        <p className="text-xs sm:text-sm text-parchment-dim mt-2 max-w-3xl">
          The inversion of Damascus Document CD IV:15-19 from an ascetic prison map into the threefold sacrament of earthly dominion.
        </p>
      </div>

      {/* Scripture Reference Quote */}
      <div className="rounded-lg border border-bronze-dark/50 bg-obsidian-deep/80 p-5 sm:p-6 space-y-2">
        <div className="text-[11px] font-mono text-gold-ancient uppercase tracking-wider">
          SCRIPTURAL EXCERPT • {QUMRAN_DATA.scriptureReference}
        </div>
        <blockquote className="font-serif italic text-xs sm:text-sm text-parchment-base/90 leading-relaxed">
          {QUMRAN_DATA.scriptureQuote}
        </blockquote>
      </div>

      {/* Comparative Matrix */}
      <div className="overflow-x-auto rounded-lg border border-iron-dark bg-obsidian-surface/60">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="border-b border-iron-dark bg-obsidian-deep/90 text-gold-ancient font-display tracking-wider uppercase text-[11px]">
              <th className="p-3 sm:p-4">The Three Nets</th>
              <th className="p-3 sm:p-4">Essene Dualist Dogma</th>
              <th className="p-3 sm:p-4">Belialian Inversion</th>
              <th className="p-3 sm:p-4">Esoteric Principle</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-iron-dark/60 font-serif">
            {QUMRAN_DATA.comparison.map((row, idx) => (
              <tr key={idx} className="hover:bg-obsidian-elevated/30 transition-colors">
                <td className="p-3 sm:p-4 font-mono font-semibold text-gold-bright">{row.net}</td>
                <td className="p-3 sm:p-4 text-parchment-dim">{row.esseneDogma}</td>
                <td className="p-3 sm:p-4 text-parchment-light font-display uppercase tracking-wide">{row.belialInversion}</td>
                <td className="p-3 sm:p-4 text-parchment-base">{row.principle}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Net Deep Dives */}
      <div className="space-y-6">
        <div className="flex flex-wrap gap-3" role="tablist" aria-label="Inverted Nets">
          {QUMRAN_DATA.nets.map((net) => (
            <button
              key={net.id}
              role="tab"
              aria-selected={activeNetId === net.id}
              onClick={() => {
                setActiveNetId(net.id);
                setCopiedProclamation(false);
              }}
              className={`px-4 py-2.5 rounded text-xs font-display tracking-wider uppercase transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright ${
                activeNetId === net.id
                  ? 'border border-gold-bright bg-obsidian-surface text-gold-bright shadow-glow-gold'
                  : 'border border-iron-dark bg-obsidian-deep text-parchment-dim hover:text-parchment-light hover:border-iron-base'
              }`}
            >
              {net.name}
            </button>
          ))}
        </div>

        <article className="chthonic-card rounded-lg p-6 sm:p-8 lg:p-10 space-y-6">
          <div className="border-b border-iron-dark/80 pb-4">
            <span className="text-[10px] font-mono text-ember-spark uppercase tracking-widest">
              SACRAMENTUM CHALCITIS
            </span>
            <h3 className="font-display text-2xl text-parchment-light mt-1">
              {currentNet.name}
            </h3>
            <div className="font-serif text-xs text-gold-bright italic mt-1">
              {currentNet.subtitle}
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-display text-xs uppercase tracking-widest text-gold-ancient">
              Theological & Exegetical Commentary
            </h4>
            <p className="text-xs sm:text-sm text-parchment-base leading-relaxed font-serif">
              {currentNet.exegesis}
            </p>
          </div>

          {/* Liturgical Proclamation */}
          <div className="rounded-lg border border-bronze-dark/60 bg-obsidian-deep/90 p-5 sm:p-6 space-y-3">
            <div className="flex items-center justify-between gap-4 border-b border-iron-dark/60 pb-3">
              <span className="font-display text-xs uppercase tracking-widest text-gold-bright">
                {currentNet.subtitle} — Liturgical Proclamation
              </span>
              <button
                onClick={() => handleCopy(currentNet.proclamation)}
                className="rounded border border-iron-base bg-obsidian-surface px-3 py-1 text-[11px] font-mono text-gold-ancient hover:border-gold-bright hover:text-gold-bright transition-colors"
              >
                {copiedProclamation ? 'PROCLAMATION COPIED' : 'COPY PROCLAMATION'}
              </button>
            </div>
            <pre className="whitespace-pre-wrap font-serif text-xs sm:text-sm text-parchment-light italic leading-relaxed pt-2">
              {currentNet.proclamation}
            </pre>
          </div>
        </article>
      </div>
    </div>
  );
};

export default QumranInversion;
